import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-components";
import { Button, Input, InputRef, message, Popconfirm, Space } from "antd";
import type { ProColumns } from "@ant-design/pro-components";
import { ReactNode, useRef, useState } from "react";
import lang from "src/lang";
import Export from "src/common/components/Export";

import { Department } from "src/modules/setting/user-management/types";
import apiRequest from "src/common/utils/apiRequest";
import coreFunctions from "src/common/utils/coreFunctions";

const DepartmentTable = ({
  actionRef,
  setActiveRow,
  openModel,
}: {
  actionRef: any;
  setActiveRow: (department: Department) => void;
  openModel: (open: boolean) => void;
}) => {
  const [allSearch, setAllSearch] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(10);
  const searchInput = useRef<InputRef>(null);
  const [tableData, setTableData] = useState<Department[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchAttributes, setSearchAttributes] = useState<
    { key: string; value: string }[]
  >([]);
  const expobj = new Export("departments, " + new Date().toDateString());

  const handleSearch = (field: string, value: string) => {
    setAllSearch("");
    const existingAttribute = searchAttributes.find(
      (attr) => attr.key === field
    );

    if (existingAttribute) {
      setSearchAttributes(
        searchAttributes.map((attr) =>
          attr.key === field ? { ...attr, value } : attr
        )
      );
    } else {
      setSearchAttributes([
        ...searchAttributes,
        {
          key: field,
          value,
        },
      ]);
    }
  };

  const searchCols = (field: string, label: string) => {
    return {
      filterDropdown: (props: any) => {
        const { close } = props;

        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.focus();
          }
        }, 100);

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder={lang.get("setting", [`Search ${label}`])}
              onChange={(e) => handleSearch(field, e.target.value)}
              value={
                searchAttributes.filter((item) => item.key === field)[0]?.value
              }
              style={{ marginBottom: 8, display: "block" }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  close();
                  if (actionRef.current) {
                    actionRef.current?.reset?.();
                    actionRef.current.reload();
                  }
                }
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                onClick={() => {
                  actionRef.current?.reset?.();
                  actionRef.current?.reload();
                }}
                style={{ width: 90 }}
              >
                {lang.get("setting", ["Search"])}
              </Button>
              <Button
                onClick={() => close()}
                size="small"
                style={{ width: 90 }}
              >
                {lang.get("setting", ["Close"])}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    };
  };

  const deleteDepartment = (id: string) => {
    const API_REQUEST = new apiRequest("department/delete", true);

    API_REQUEST.send(
      { id },
      (response: { status: string; message: string }) => {
        if (response.status === "success") {
          message.success(response.message);
          actionRef.current?.reload();
        } else {
          message.error(response.message);
        }
      }
    );
  };

  const columns: ProColumns<Department>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: false,
      render: (id: ReactNode, record, index) => <span>{index + 1}</span>,
      align: "left",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (name: ReactNode) => <span>{name}</span>,
      align: "left",
      width: 250,
      ...searchCols("name", "Name"),
    },
    {
      title: "Department Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (email: ReactNode) => <span>{email}</span>,
      align: "left",
      width: 300,
      ...searchCols("email", "Department Email"),
    },

    {
      title: "Options",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setActiveRow(record);
              openModel(true);
            }}
          />
          <Popconfirm
            title="Are you sure to delete this department?"
            onConfirm={() => {
              deleteDepartment(record.id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <ProTable
        headerTitle={lang.get("setting", ["Departments"])}
        columns={columns}
        actionRef={actionRef}
        dataSource={tableData}
        request={async (params, sort, filter) => {
          await new coreFunctions().waitRequest(10);

          const processedFilters = Object.entries(filter || {}).reduce(
            (acc, [key, value]) => {
              if (value && value.length > 0) {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, (string | number)[]>
          );

          const payload = {
            params: params,
            sorts: sort,
            filters: processedFilters,
            searches: searchAttributes,
            all_search: allSearch,
          };

          const API_REQUEST = new apiRequest("departments/get", true);
          const response = await API_REQUEST.send(payload);

          if (response && response.status === "success") {
            const fetchedData = response.data || [];
            setTableData(fetchedData);
            setTotalRecords(response.total);

            return {
              data: fetchedData,
              total: totalRecords,
              success: true,
            };
          }

          return {
            data: [],
            total: 0,
            success: false,
          };
        }}
        rowKey="id"
        pagination={{
          defaultCurrent: 1,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          showTotal: (total, range) =>
            lang.get("setting", [
              `Showing ${range[0]}-${range[1]} of ${total} entries`,
            ]),
          onShowSizeChange: (current, size) => {
            setPageSize(size);
          },
        }}
        search={false}
        scroll={{ x: 800 }}
        cardBordered
        toolBarRender={() => [
          <Input
            style={{ width: "auto" }}
            key="all_search"
            allowClear
            placeholder={lang.get("setting", ["Global Search"])}
            value={allSearch}
            onChange={(e) => {
              setAllSearch(e.target.value);
              setSearchAttributes([]);
              actionRef?.current?.reload();
            }}
          />,
          <Button
            key="csv"
            onClick={() => {
              const csvData = tableData.map((row) => ({
                [lang.get("setting", ["Name"])]: row.name,
                [lang.get("setting", ["Email"])]: row.email,
              }));
              expobj.exportToCSV(csvData);
            }}
          >
            {lang.get("setting", ["Export CSV"])}
          </Button>,
          <Button
            key="excel"
            onClick={() => {
              const excelData = tableData.map((row) => ({
                [lang.get("setting", ["Name"])]: row.name,
                [lang.get("setting", ["Email"])]: row.email,
              }));
              expobj.exportToExcel(excelData);
            }}
          >
            {lang.get("setting", [" Excel"])}
          </Button>,
          <Button
            key="pdf"
            onClick={() => {
              expobj.exportToPDF({
                head: [
                  [
                    lang.get("setting", ["ID"]),
                    lang.get("setting", ["Name"]),
                    lang.get("setting", ["Email"]),
                  ],
                ],
                body: tableData.map((row) => [row.name, row.email]),
              });
            }}
          >
            {lang.get("setting", ["Export PDF"])}
          </Button>,
        ]}
      />
    </div>
  );
};

export default DepartmentTable;
